import React, { useState, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { collection, addDoc, setDoc, serverTimestamp } from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { db, storage } from '../firebase/config';
import './Register.css';

const SPORTS = ['Baseball','Basketball','Field Hockey','Football','Ice Hockey','Lacrosse','Soccer','Softball','Volleyball'];
const US_STATES = ['AL','AK','AZ','AR','CA','CO','CT','DE','FL','GA','HI','ID','IL','IN','IA','KS','KY','LA','ME','MD','MA','MI','MN','MS','MO','MT','NE','NV','NH','NJ','NM','NY','NC','ND','OH','OK','OR','PA','RI','SC','SD','TN','TX','UT','VT','VA','WA','WV','WI','WY'];

const MAPS_API_KEY      = 'AIzaSyBszVnZP3yJE601w8cw4CpyF9WBGr_Unrc';
const GEOCODING_API_KEY = 'AIzaSyDUiFvO1FtkQCIfLIAWc2GR9TcLDZRrC50';

export default function Register() {
  const [params] = useSearchParams();
  const [tab, setTab] = useState(params.get('type') === 'event' ? 'event' : 'team');
  const [form, setForm] = useState({
    name:'', city:'', state:'', sport:'', locationInput:'',
    website:'', facebook:'', instagram:'',
    contactFirst:'', contactLast:'', contactEmail:'',
    startDate:'', endDate:'',
  });
  const [logo, setLogo] = useState(null);
  const [logoPreview, setLogoPreview] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState('');
  const [suggestions, setSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [highlightedIndex, setHighlightedIndex] = useState(-1);

  const autocompleteServiceRef = React.useRef(null);

  // Load Google Maps script once
  React.useEffect(() => {
    if (window.google) return;
    const script = document.createElement('script');
    script.src = `https://maps.googleapis.com/maps/api/js?key=${MAPS_API_KEY}&libraries=places`;
    script.async = true;
    script.onload = () => { autocompleteServiceRef.current = new window.google.maps.places.AutocompleteService(); };
    document.head.appendChild(script);
  }, []);

  const handleLocationInput = async (value) => {
    set('locationInput', value);
    set('city', '');
    set('state', '');
    if (value.length < 2) { setSuggestions([]); setShowSuggestions(false); return; }
    try {
      if (!window.google) return;
      const { AutocompleteSuggestion } = window.google.maps.places;
      const request = { input: value, includedRegionCodes: ['us'], includedPrimaryTypes: ['locality'] };
      const { suggestions } = await AutocompleteSuggestion.fetchAutocompleteSuggestions(request);
      const preds = suggestions.map(s => ({
        description: s.placePrediction.text.toString(),
        place_id: s.placePrediction.placeId,
      }));
      setSuggestions(preds);
      setShowSuggestions(preds.length > 0);
      setHighlightedIndex(-1);
    } catch (err) {
      const service = autocompleteServiceRef.current ||
        (window.google ? new window.google.maps.places.AutocompleteService() : null);
      if (!service) return;
      service.getPlacePredictions(
        { input: value, types: ['(cities)'], componentRestrictions: { country: 'us' } },
        (predictions, status) => {
          if (status === 'OK' && predictions) {
            setSuggestions(predictions.map(p => ({ description: p.description, place_id: p.place_id })));
            setShowSuggestions(true);
            setHighlightedIndex(-1);
          } else {
            setSuggestions([]);
            setHighlightedIndex(-1);
          }
        }
      );
    }
  };

  const selectSuggestion = (s) => {
    const parts = s.description.split(',');
    const city  = parts[0]?.trim() || '';
    const state = parts[1]?.trim().split(' ')[0] || '';
    set('city', city);
    set('state', state);
    set('locationInput', s.description);
    setSuggestions([]);
    setShowSuggestions(false);
    setHighlightedIndex(-1);
  };

  const fileRef = useRef();
  const set = (k, v) => setForm(f => ({...f, [k]: v}));

  const handleFile = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setLogo(file);
    setLogoPreview(URL.createObjectURL(file));
  };

  // Geocode city once at registration time
  const geocodeCity = async (city, state) => {
    try {
      const res = await fetch(
        `https://maps.googleapis.com/maps/api/geocode/json?address=${encodeURIComponent(`${city}, ${state}`)}&key=${GEOCODING_API_KEY}`
      );
      const data = await res.json();
      if (data.status === 'OK') {
        const loc = data.results[0].geometry.location;
        return { lat: loc.lat, lng: loc.lng };
      }
    } catch (_) {}
    return null;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!logo) { setError('Please upload a logo.'); return; }
    if (!form.sport) { setError('Please select a sport.'); return; }
    setSubmitting(true);
    setError('');

    try {
      const colName = tab === 'team' ? 'teams' : 'events';

      const { doc } = await import('firebase/firestore');
      const newDocRef = doc(collection(db, colName));
      const id = newDocRef.id;

      // Upload logo
      const storageRef = ref(storage, `${colName === 'teams' ? 'team' : 'event'}_logos/${id}.jpg`);
      await uploadBytes(storageRef, logo);
      const logoUrl = await getDownloadURL(storageRef);

      const qrCode = `https://the-patch-guys.com/${tab}/${id}`;

      // Geocode city at registration time so users never have to
      const coords = await geocodeCity(form.city.trim(), form.state);

      const data = {
        id,
        name:             form.name.trim(),
        city:             form.city.trim(),
        state:            form.state,
        sport:            form.sport,
        website:          form.website.trim(),
        facebook:         form.facebook.trim(),
        instagram:        form.instagram.trim(),
        contactFirstName: form.contactFirst.trim(),
        contactLastName:  form.contactLast.trim(),
        contactEmail:     form.contactEmail.trim(),
        logoUrl,
        qrCode,
        approved:         false,
        createdAt:        serverTimestamp(),
        ...(coords ? { lat: coords.lat, lng: coords.lng } : {}),
        ...(tab === 'event' ? {
          startDate: form.startDate ? new Date(form.startDate + 'T12:00:00') : null,
          endDate:   form.endDate   ? new Date(form.endDate   + 'T12:00:00') : null,
        } : {}),
      };

      await setDoc(newDocRef, data);

      setSubmitted(true);
    } catch (err) {
      console.error(err);
      setError('Something went wrong. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  if (submitted) return (
    <div className="register">
      <section className="register__success">
        <div className="container register__success-inner">
          <div className="register__success-icon">✅</div>
          <h2>Registration Submitted!</h2>
          <p>
            Thank you for registering your {tab}. Our team will review your submission
            and approve it within 1–2 business days. Once approved, your QR code will be live!
          </p>
          <button className="btn btn-primary btn-lg" onClick={() => { setSubmitted(false); setForm({name:'',city:'',state:'',sport:'',locationInput:'',website:'',facebook:'',instagram:'',contactFirst:'',contactLast:'',contactEmail:'',startDate:'',endDate:''}); setLogo(null); setLogoPreview(null); }}>
            Submit Another
          </button>
        </div>
      </section>
    </div>
  );

  return (
    <div className="register">
      <section className="register__hero">
        <div className="container">
          <div className="badge badge-gold" style={{marginBottom:'20px'}}>Get Your QR Code</div>
          <h1 className="section-title" style={{color:'white', marginBottom:'16px'}}>Register Your Team or Event</h1>
          <p style={{color:'rgba(255,255,255,0.65)', fontSize:'17px', maxWidth:'520px', margin:'0 auto', textAlign:'center', lineHeight:'1.7'}}>
            Join the platform and get a unique QR code. Players scan it after games to collect your patch.
          </p>
        </div>
      </section>

      <section className="section register__form-section">
        <div className="container register__form-wrap">
          {/* Tabs */}
          <div className="register__tabs">
            <button
              className={`register__tab ${tab === 'team' ? 'register__tab--active' : ''}`}
              onClick={() => setTab('team')}
            >
              🛡️ Register a Team
            </button>
            <button
              className={`register__tab ${tab === 'event' ? 'register__tab--active' : ''}`}
              onClick={() => setTab('event')}
            >
              🎫 Register an Event
            </button>
          </div>

          <form onSubmit={handleSubmit} className="register__form">
            {/* Name */}
            <div className="form-group">
              <label className="form-label">{tab === 'team' ? 'Team / Club Name' : 'Event Name'} *</label>
              <input className="form-input" required value={form.name} onChange={e => set('name', e.target.value)} placeholder={tab === 'team' ? 'e.g. Orlando FC' : 'e.g. Sunshine Showcase 2025'} />
            </div>

            {/* Sport */}
            <div className="form-group">
              <label className="form-label">Sport *</label>
              <select className="form-select" required value={form.sport} onChange={e => set('sport', e.target.value)}>
                <option value="">Select a sport...</option>
                {SPORTS.map(s => <option key={s} value={s}>{s}</option>)}
              </select>
            </div>

            {/* Location */}
            <div className="form-group">
              <label className="form-label">City, State *</label>
              <div className="location-wrap">
                <input
                  className="form-input"
                  required={!form.city}
                  value={form.locationInput || ''}
                  onChange={e => handleLocationInput(e.target.value)}
                  onFocus={() => suggestions.length > 0 && setShowSuggestions(true)}
                  onBlur={() => setTimeout(() => setShowSuggestions(false), 200)}
                  onKeyDown={e => {
                    if (!showSuggestions || suggestions.length === 0) return;
                    if (e.key === 'ArrowDown') {
                      e.preventDefault();
                      setHighlightedIndex(i => Math.min(i + 1, suggestions.length - 1));
                    } else if (e.key === 'ArrowUp') {
                      e.preventDefault();
                      setHighlightedIndex(i => Math.max(i - 1, 0));
                    } else if (e.key === 'Enter') {
                      e.preventDefault();
                      if (highlightedIndex >= 0) selectSuggestion(suggestions[highlightedIndex]);
                    } else if (e.key === 'Escape') {
                      setShowSuggestions(false);
                      setHighlightedIndex(-1);
                    }
                  }}
                  placeholder="Start typing a city..."
                  autoComplete="off"
                />
                {form.city && <span className="location-check">✓</span>}
                {showSuggestions && suggestions.length > 0 && (
                  <ul className="location-dropdown">
                    {suggestions.map((s, i) => (
                      <li key={i} onMouseDown={() => selectSuggestion(s)} className={`location-dropdown__item${highlightedIndex === i ? ' location-dropdown__item--highlighted' : ''}`}>
                        <span>📍</span> {s.description}
                      </li>
                    ))}
                  </ul>
                )}
              </div>
              {form.city && (
                <p className="location-hint">
                  ✓ <strong>{form.city}</strong>, <strong>{form.state}</strong>
                </p>
              )}
            </div>

            {/* Event dates */}
            {tab === 'event' && (
              <div className="register__form-row">
                <div className="form-group">
                  <label className="form-label">Start Date *</label>
                  <input className="form-input" type="date" required value={form.startDate} onChange={e => set('startDate', e.target.value)} />
                </div>
                <div className="form-group">
                  <label className="form-label">End Date *</label>
                  <input className="form-input" type="date" required value={form.endDate} onChange={e => set('endDate', e.target.value)} />
                </div>
              </div>
            )}

            {/* Social / web */}
            <div className="register__form-section-label">Website & Social Media <span>(Optional)</span></div>
            <div className="form-group">
              <label className="form-label">Website</label>
              <input className="form-input" type="url" value={form.website} onChange={e => set('website', e.target.value)} placeholder="https://yoursite.com" />
            </div>
            <div className="register__form-row">
              <div className="form-group">
                <label className="form-label">Facebook URL</label>
                <input className="form-input" type="url" value={form.facebook} onChange={e => set('facebook', e.target.value)} placeholder="https://facebook.com/..." />
              </div>
              <div className="form-group">
                <label className="form-label">Instagram URL</label>
                <input className="form-input" type="url" value={form.instagram} onChange={e => set('instagram', e.target.value)} placeholder="https://instagram.com/..." />
              </div>
            </div>

            {/* Contact */}
            <div className="register__form-section-label">Contact Information *</div>
            <div className="register__form-row">
              <div className="form-group">
                <label className="form-label">First Name *</label>
                <input className="form-input" required value={form.contactFirst} onChange={e => set('contactFirst', e.target.value)} />
              </div>
              <div className="form-group">
                <label className="form-label">Last Name *</label>
                <input className="form-input" required value={form.contactLast} onChange={e => set('contactLast', e.target.value)} />
              </div>
            </div>
            <div className="form-group">
              <label className="form-label">Contact Email *</label>
              <input className="form-input" type="email" required value={form.contactEmail} onChange={e => set('contactEmail', e.target.value)} placeholder="you@example.com" />
            </div>

            {/* Logo */}
            <div className="register__form-section-label">Logo *</div>
            <div className="register__logo-upload" onClick={() => fileRef.current.click()}>
              {logoPreview
                ? <img src={logoPreview} alt="Logo preview" className="register__logo-preview" />
                : <>
                    <div className="register__logo-icon">📷</div>
                    <div className="register__logo-text">Click to upload logo</div>
                    <div className="register__logo-hint">JPG or PNG recommended</div>
                  </>
              }
              <input ref={fileRef} type="file" accept="image/*" onChange={handleFile} style={{display:'none'}} />
            </div>

            {error && <div className="register__error">{error}</div>}

            <button type="submit" className="btn btn-primary btn-lg register__submit" disabled={submitting}>
              {submitting ? 'Submitting...' : `Submit ${tab === 'team' ? 'Team' : 'Event'} Registration`}
            </button>
          </form>
        </div>
      </section>
    </div>
  );
}